import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, ChevronDown, Calendar, MapPin, Clock, Users, Award, Sparkles, Star, ArrowRight, Ticket } from 'lucide-react';
import { getShows, getNews, getStats } from '../services/cmsData';

// Hero Section with Video Background
export const HeroSection = () => {
  const [currentText, setCurrentText] = useState(0);
  const heroTexts = [
    "Unforgettable Family Theatre",
    "Magical Moments on Stage", 
    "Your Favourite Characters Live"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % heroTexts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight - 100,
      behavior: 'smooth'
    });
  };

  return (
    <section 
      data-testid="hero-section" 
      className="relative h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Video Background */}
      <div className="absolute inset-0 bg-brand-dark">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="https://streamable.com/l/2kpp4z/mp4.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/60 via-brand-dark/40 to-brand-dark" />
        <div className="absolute inset-0 bg-brand-primary/10" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-accent text-accent-gold text-sm sm:text-base tracking-[0.4em] mb-8 uppercase">
            Mark Thompson Productions Presents
          </p>
        </motion.div>

        <div className="h-40 sm:h-52 flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.h1
              key={currentText}
              initial={{ opacity: 0, y: 60, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -60, scale: 0.95 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white text-shadow-lg leading-tight pb-2"
            >
              {heroTexts[currentText]}
            </motion.h1>
          </AnimatePresence>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="font-body text-sm sm:text-base md:text-lg text-white/70 mt-6 max-w-3xl mx-auto tracking-wide text-center px-4"
        >
          Taking well-known pre-school, family and education brands from page and screen to stage and creating magical memories for the entire family
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 flex flex-col sm:flex-row gap-5 justify-center"
        >
          <Link
            to="/shows"
            data-testid="hero-explore-shows-btn"
            className="group inline-flex items-center justify-center gap-3 border-2 border-white/50 text-white px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:bg-white hover:text-brand-dark hover:border-white hover:scale-105 backdrop-blur-sm"
          >
            <Sparkles className="w-5 h-5" />
            Explore Our Shows
          </Link>
          <Link
            to="/tour-dates"
            data-testid="hero-book-tickets-btn"
            className="group inline-flex items-center justify-center gap-3 border-2 px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4AF37'; e.currentTarget.style.color = '#020120'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#D4AF37'; }}
          >
            <Ticket className="w-5 h-5" />
            Find Tickets
          </Link>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        onClick={scrollToContent}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/70 hover:text-accent-gold transition-colors focus-ring rounded-full p-3"
        aria-label="Scroll to content"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-10 h-10" />
        </motion.div>
      </motion.button>
    </section>
  );
};

// Featured Shows - Only Currently Touring (Family)
export const FeaturedShowsCarousel = () => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShows = async () => {
      try {
        // Get only currently touring family shows
        const data = await getShows({ status: 'current', category: 'family' });
        setShows(data);
      } catch (error) {
        console.error('Error fetching shows:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchShows();
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[3/4] bg-gray-200 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="featured-shows-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-accent text-brand-primary text-sm tracking-[0.3em] mb-4 uppercase">
            Now Touring
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-dark leading-tight">
            The MT Family Shows<br />
            <span className="text-brand-primary">Now Touring</span>
          </h2>
        </motion.div>

        {/* All Shows Grid - No rounded edges on posters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 lg:gap-6">
          {shows.map((show, index) => (
            <motion.div
              key={show.id}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ 
                delay: index * 0.05, 
                duration: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              whileHover={{ y: -8, transition: { duration: 0.25, ease: "easeOut" } }}
            >
              <Link
                to={`/shows/${show.id}`}
                data-testid={`show-card-${show.id}`}
                className="block group"
              >
                <div className="relative aspect-[3/4] overflow-hidden shadow-lg transition-all duration-300 group-hover:shadow-2xl">
                  <img
                    src={`${process.env.PUBLIC_URL}${show.poster}`}
                    alt={show.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/30 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 lg:p-4">
                    <h3 className="font-display text-sm lg:text-base font-bold text-white mb-1 group-hover:text-accent-gold transition-colors duration-300 line-clamp-2">
                      {show.shortTitle}
                    </h3>
                    <p className="text-white/70 text-xs hidden sm:block line-clamp-1">
                      {show.tagline}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/shows"
            data-testid="view-all-shows-btn"
            className="inline-flex items-center gap-3 text-brand-primary font-semibold text-lg hover:text-brand-dark transition-colors group"
          >
            View All Productions
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// MT Big People Shows - Now Touring
export const BigPeopleShowsCarousel = () => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShows = async () => {
      try {
        const data = await getShows({ category: 'big-people' });
        setShows(data);
      } catch (error) {
        console.error('Error fetching big people shows:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchShows();
  }, []);

  if (loading || shows.length === 0) return null;

  return (
    <section data-testid="big-people-shows-section" className="py-20 lg:py-28 bg-gray-50 relative overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-accent text-brand-primary text-sm tracking-[0.3em] mb-4 uppercase">
            For Grown-Ups
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-dark leading-tight">
            The MT Big People Shows<br />
            <span className="text-brand-primary">Now Touring</span>
          </h2>
        </motion.div>

        {/* Big People Shows Grid - same poster styling as family shows */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 lg:gap-6 justify-items-center">
          {shows.map((show, index) => (
            <motion.div
              key={show.id}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{
                delay: index * 0.05,
                duration: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              whileHover={{ y: -8, transition: { duration: 0.25, ease: "easeOut" } }}
              className="w-full"
            >
              <Link
                to={`/shows/${show.id}`}
                data-testid={`big-people-show-card-${show.id}`}
                className="block group"
              >
                <div className="relative aspect-[3/4] overflow-hidden shadow-lg transition-all duration-300 group-hover:shadow-2xl">
                  <img
                    src={`${process.env.PUBLIC_URL}${show.poster}`}
                    alt={show.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/30 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 lg:p-4">
                    <h3 className="font-display text-sm lg:text-base font-bold text-white mb-1 group-hover:text-accent-gold transition-colors duration-300 line-clamp-2">
                      {show.shortTitle}
                    </h3>
                    <p className="text-white/70 text-xs hidden sm:block line-clamp-1">
                      {show.tagline}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section - LIVE DATA from API
export const StatsSection = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const displayStats = stats ? [
    { value: stats.remainingShows, label: 'Shows Remaining', icon: Calendar, suffix: '' },
    { value: stats.totalShowsThisYear, label: 'Shows This Year', icon: Sparkles, suffix: '' },
    { value: stats.uniqueTowns, label: 'Towns & Cities', icon: MapPin, suffix: '+' },
    { value: stats.activeProductions, label: 'Live Productions', icon: Award, suffix: '' },
  ] : [
    { value: '---', label: 'Shows Remaining', icon: Calendar, suffix: '' },
    { value: '---', label: 'Shows This Year', icon: Sparkles, suffix: '' },
    { value: '---', label: 'Towns & Cities', icon: MapPin, suffix: '' },
    { value: '---', label: 'Live Productions', icon: Award, suffix: '' },
  ];

  return (
    <section data-testid="stats-section" className="py-20 lg:py-28 bg-gradient-to-br from-brand-primary to-brand-secondary relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-accent-gold/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-accent text-accent-gold text-sm tracking-[0.3em] uppercase mb-4">
            Live Tour Stats
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            The Numbers Behind The Magic
          </h2>
          <p className="text-white/80 text-lg lg:text-xl">
            Playing to over <span className="text-accent-gold font-bold">400 UK Theatres</span> each year
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {displayStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center group"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white/10 flex items-center justify-center transition-all duration-300 group-hover:bg-accent-gold group-hover:scale-110 group-hover:rotate-3">
                <stat.icon className="w-10 h-10 text-accent-gold group-hover:text-brand-dark transition-colors" />
              </div>
              <div className="font-display text-5xl lg:text-6xl font-bold text-white mb-3">
                {loading ? (
                  <span className="animate-pulse">---</span>
                ) : (
                  <>{stat.value}{stat.suffix}</>
                )}
              </div>
              <div className="font-body text-white/70 text-base">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Next Show Teaser */}
        {stats?.nextShow && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-full px-8 py-4">
              <span className="text-accent-gold font-semibold">Next Show:</span>
              <span className="text-white">
                {stats.nextShow.showShortTitle || stats.nextShow.showTitle} at {stats.nextShow.venue}, {stats.nextShow.town}
              </span>
              <span className="text-white/60">
                {new Date(stats.nextShow.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// News Bento Grid - Only 2 items
export const NewsBentoGrid = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await getNews({ limit: 2 });
        setNews(data);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {[1, 2].map((i) => (
              <div key={i} className="bg-gray-200 rounded-3xl h-96 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="news-section" className="py-24 lg:py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16"
        >
          <div>
            <p className="font-accent text-brand-primary text-sm tracking-[0.3em] mb-4 uppercase">
              What's Happening
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-dark">
              Latest News
            </h2>
          </div>
          <Link
            to="/news"
            data-testid="view-all-news-link"
            className="mt-8 lg:mt-0 inline-flex items-center gap-3 text-brand-primary font-semibold text-lg hover:text-brand-dark transition-colors group"
          >
            View All News
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
          </Link>
        </motion.div>

        {/* Two Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {news.slice(0, 2).map((article, index) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="group"
            >
              <Link 
                to={`/news/${article.id}`}
                data-testid={`news-card-${article.id}`}
                className="block bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3"
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={`${process.env.PUBLIC_URL}${article.image}`}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-5 left-5">
                    <span className="px-5 py-2 rounded-full bg-accent-gold text-brand-dark text-sm font-bold uppercase tracking-wider shadow-lg">
                      {article.category.replace('-', ' ')}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <time className="font-body text-sm text-brand-primary font-medium">
                    {new Date(article.date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </time>
                  <h3 className="font-display text-2xl font-bold text-brand-dark group-hover:text-brand-primary transition-colors mt-3">
                    {article.title}
                  </h3>
                  <p className="font-body text-gray-600 mt-4 text-lg line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-brand-primary font-semibold group-hover:gap-4 transition-all">
                    Read More
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Teaser - DARK with big imagery
export const AboutTeaser = () => {
  return (
    <section data-testid="about-teaser-section" className="relative bg-brand-dark z-10">
      {/* Background Pattern - contained */}
      <div className="absolute inset-0 overflow-hidden opacity-5 pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 border border-white rounded-full" />
        <div className="absolute bottom-20 left-20 w-96 h-96 border border-white rounded-full" />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-accent text-accent-gold text-sm tracking-[0.3em] mb-6 uppercase">
              Our Story
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8">
              Creating Magical<br />
              Memories Since<br />
              <span className="text-accent-gold">2013</span>
            </h2>
            <p className="font-body text-white/70 text-xl leading-relaxed mb-8">
              Taking well-known pre-school, family and education brands from page and screen to stage and creating magical memories for the entire family.
            </p>
            <div className="flex flex-wrap gap-6">
              <Link
                to="/history"
                data-testid="about-learn-more-btn"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#D4AF37', color: '#020120' }}
              >
                Discover Our Journey
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:bg-white hover:text-brand-dark hover:border-white"
              >
                Get in Touch
              </Link>
            </div>
          </div>

          {/* London Theatre Photo - Polaroid style frame */}
          <div className="relative flex items-end justify-center mb-[-60px] lg:mb-[-80px] z-20">
            <div className="relative transform rotate-2 hover:rotate-0 transition-transform duration-500">
              {/* Polaroid frame */}
              <div className="bg-white p-3 pb-14 shadow-[0_25px_60px_rgba(0,0,0,0.4)] rounded-sm">
                <img
                  src={`${process.env.PUBLIC_URL}/cutarounds/london-savoy-theatre.jpg`}
                  alt="Ministry of Science at the Savoy Theatre, London"
                  className="w-full max-w-sm lg:max-w-md object-cover rounded-sm"
                />
                {/* Polaroid caption */}
                <p className="absolute bottom-4 left-0 right-0 text-center font-accent text-sm text-gray-600 tracking-wide">
                  Savoy Theatre, London 2023
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Instagram Feed Section
export const CTASection = () => {
  useEffect(() => {
    // Load Elfsight script
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      // Cleanup if needed
    };
  }, []);

  return (
    <section data-testid="instagram-section" className="py-12 lg:py-16 bg-gray-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Elfsight Instagram Feed Widget */}
        <div className="flex justify-center">
          <div className="elfsight-app-b93c5ee0-ab87-43d9-b544-050180133209" data-elfsight-app-lazy></div>
        </div>

        {/* Follow Button */}
        <div className="text-center mt-6">
          <a
            href="https://instagram.com/mark_thompson_productions"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-8 py-3 rounded-full font-bold text-base transition-all duration-300 hover:scale-105 hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            Follow on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

// Clients/Partners Section - Auto-scrolling slider
export const ClientsSection = () => {
  const allLogos = [
    { src: '/clients/science-museum-th.jpg', alt: 'Science Museum' },
    { src: '/clients/nhm-th.jpg', alt: 'Natural History Museum' },
    { src: '/clients/uk-space-agency-th.png', alt: 'UK Space Agency' },
    { src: '/clients/kew-gardens-th.png', alt: 'Kew Royal Botanic Gardens' },
    { src: '/clients/bbc-th.jpg', alt: 'BBC' },
    { src: '/clients/mattel-th.jpg', alt: 'Mattel' },
    { src: '/clients/paramount-th.png', alt: 'Paramount' },
    { src: '/clients/5-broadcasting-th.jpg', alt: 'Channel 5' },
    { src: '/clients/tg-entertainment-th.jpg', alt: 'TG Entertainment' },
    { src: '/clients/trafalgar-theatres-th.png', alt: 'Trafalgar Theatres' },
    { src: '/clients/f1-th.jpg', alt: 'Formula 1' },
    { src: '/clients/bt-th.jpg', alt: 'BT' },
    { src: '/clients/pando-th.jpg', alt: 'P&O Cruises' },
    { src: '/clients/butlins-th.jpg', alt: 'Butlins' },
    { src: '/clients/edenlogo-th.jpg', alt: 'Eden Project' },
    { src: '/clients/british-th.jpg', alt: 'British Council' },
    { src: '/clients/guiness-th.jpg', alt: 'Guinness World Records' },
    { src: '/clients/atg2-th.jpg', alt: 'ATG Tickets' },
    { src: '/clients/newunderbelly-th.jpg', alt: 'Underbelly' },
    { src: '/clients/edinburgh-th.jpg', alt: 'Edinburgh Science Festival' },
    { src: '/clients/big-bang-fair-th.jpg', alt: 'Big Bang Fair' },
    { src: '/clients/hong-kong-th.jpg', alt: 'Hong Kong Science Museum' },
    { src: '/clients/winchester-th.jpg', alt: 'Winchester Science Centre' },
    { src: '/clients/carnival-uk-th.jpg', alt: 'Carnival UK' },
    { src: '/clients/newsky-th.jpg', alt: 'Sky' },
    { src: '/clients/bahrain-th.jpg', alt: 'Bahrain' },
    { src: '/clients/mubadala-th.jpg', alt: 'Mubadala' },
    { src: '/clients/sharjah-th.jpg', alt: 'Sharjah' },
    { src: '/clients/adsf-th.jpg', alt: 'Abu Dhabi Science Festival' },
    { src: '/clients/rugby-world-cup-th.jpg', alt: 'Rugby World Cup' },
    { src: '/clients/engineering-uk-th.jpg', alt: 'Engineering UK' },
    { src: '/clients/ulster-th.jpg', alt: 'Ulster Museum' },
    { src: '/clients/hq3-th.jpg', alt: 'HQ Theatres' },
    { src: '/clients/newbunnleisure-th.jpg', alt: 'Bunn Leisure' },
  ];

  // Split logos into two rows for two-direction scrolling
  const row1 = allLogos.slice(0, 17);
  const row2 = allLogos.slice(17);

  return (
    <section data-testid="clients-section" className="py-16 lg:py-20 bg-gray-50 relative overflow-hidden">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="font-accent text-brand-primary text-sm tracking-[0.3em] uppercase mb-3">
            Trusted Worldwide
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-dark">
            Our Partners & Clients
          </h2>
        </motion.div>
      </div>

      {/* Scrolling Logo Rows */}
      <div className="space-y-6">
        {/* Row 1 - Scrolls Left */}
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10" />
          <motion.div
            animate={{ x: [0, -1680] }}
            transition={{ 
              x: { repeat: Infinity, repeatType: "loop", duration: 30, ease: "linear" }
            }}
            className="flex gap-6"
          >
            {[...row1, ...row1].map((logo, i) => (
              <div 
                key={i}
                className="flex-shrink-0 w-32 h-20 lg:w-40 lg:h-24 flex items-center justify-center bg-white rounded-xl shadow-md p-3 hover:shadow-lg transition-shadow duration-300"
              >
                <img 
                  src={`${process.env.PUBLIC_URL}${logo.src}`} 
                  alt={logo.alt}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Row 2 - Scrolls Right */}
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10" />
          <motion.div
            animate={{ x: [-1680, 0] }}
            transition={{ 
              x: { repeat: Infinity, repeatType: "loop", duration: 35, ease: "linear" }
            }}
            className="flex gap-6"
          >
            {[...row2, ...row2, ...row2].map((logo, i) => (
              <div 
                key={i}
                className="flex-shrink-0 w-32 h-20 lg:w-40 lg:h-24 flex items-center justify-center bg-white rounded-xl shadow-md p-3 hover:shadow-lg transition-shadow duration-300"
              >
                <img 
                  src={`${process.env.PUBLIC_URL}${logo.src}`} 
                  alt={logo.alt}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
